<template>
  <div class="container admin-page-with-back-py">
    <div class="mb-2">
      <button type="button" class="p-0 back-button" style="border: none; background: transparent;"
              @click="handleBackButton">
        <BackIcon/>
        Back
      </button>
    </div>

    <div class="row">
      <div class="col-12 col-lg-8 offset-lg-2">
        <div class="row">
          <div class="col-12" v-if="task.completed_at === null && requiresScreenshots">
            <div class="note">
              * Please note that if there no action before the end of the day, system will submit all screenshots taken
              for the task playable session as logs.
            </div>
          </div>
          <div class="col-12">
            <div class="task-details-title">
              {{ task.title }}
            </div>
          </div>
          <div class="col-12">
            <div class="task-details-description">
              {{ task.description }}
            </div>
          </div>
          <div class="col-12 mt-5">
            <div class="task-details-company">
              <span
                  style="display: inline-block;border-radius: 50%;height: 8px;width: 8px;background-color: #2ecc71;"></span>
              {{ task.company.name }}
            </div>
          </div>
        </div>

        <div class="row my-4">
          <div class="col-12 col-md-8">
            <div class="row">
              <div class="col-6 mb-3">
                <div class="task-details-estimation-label">Due</div>
                <div class="task-details-estimation">{{ dueDate }}</div>
              </div>
              <div class="col-6 mb-3">
                <div class="task-details-estimation-label">Sub Tasks</div>
                <div class="task-details-estimation">{{ task.subtasks.length }} Tasks</div>
              </div>
              <div class="col-6 mb-3">
                <div class="task-details-estimation-label">Hours Est.</div>
                <div class="task-details-estimation">{{ hourEstimation }}</div>
              </div>
              <div class="col-6 mb-3">
                <div class="task-details-estimation-label">Pay Est.</div>
                <div class="task-details-estimation">{{ totalCost }}</div>
              </div>
              <div class="col-6 mb-3">
                <div class="task-details-estimation-label">Total Hours Worked</div>
                <div class="task-details-estimation">{{
                    minutesToHoursFormatter(task.total_minutes_spent)
                  }}</div>
              </div>
              <div class="col-6 mb-3">
                <div class="task-details-estimation-label">Total Pay</div>
                <div class="task-details-estimation">{{ trueInternalCost }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="row my-4" v-if="subtasks.length > 0">
          <div class="col-12">
            <div class="task-details-title">{{ subtasks.length }} Subtasks</div>
          </div>
        </div>

        <staff-task-item v-for="task of subtasks" :task="task" :key="task.id" :isSubtask="true"/>
      </div>
    </div>

    <div class="row">
      <div class="col-12 col-lg-8 offset-lg-2">
        <div class="task-details-title mb-2">Time Log</div>

        <div class="row">
          <div class="col-12">
            <div v-for="(log, logKey) of logs">
              <staff-task-log
                  :key="logKey"
                  :log="log"
                  @deleteScreenshot="(key, screenshot) => deleteScreenshot(logKey, key, screenshot)"
              ></staff-task-log>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div v-if="loading" class="d-block position-relative">
              <p class="d-flex">
                <span class="spinner-border mr-2" role="status">
                  <span class="sr-only">Loading...</span>
                </span>
                Loading time log data...
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-5" v-if="requireSubmission && requiresScreenshots">
      <div class="offset-md-3 col-12 col-md-3 text-right">
        <button
            @click="cancelEditing"
            type="button"
            class="btn-block wfh-btn wfh-btn-large wfh-btn-primary-outline"
            :disabled="!requireSubmission"
        >
          Cancel
        </button>
      </div>
      <div class="col-12 col-md-3 text-left">
        <button
            @click="submitLogs"
            type="button"
            class="btn-block wfh-btn wfh-btn-large wfh-btn-primary"
            :disabled="!requireSubmission"
        >
          Submit Logs
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import BackIcon from '@icons/BackIcon';
import { mapState } from 'vuex';
import minutesToHoursFormatter from '../../../utils/minutesToHoursFormatter';

export default {
  components: { BackIcon },

  props: {
    task: {
      required: true,
    },
  },

  data () {
    return {
      loading: true,
      originalLogs: [],
      logs: [],
      deletedScreenshots: {},
      requireSubmission: false,
    };
  },

  computed: {
    hourEstimation () {
      if (this.task.task_cost === null || this.task.task_cost.hour_estimation === null) {
        return 'N/A';
      }

      return this.task.task_cost.hour_estimation.toLocaleString();
    },

    totalCost () {
      if (this.task.task_cost === null || this.task.task_cost.payment_currency === null) {
        return 'N/A';
      }

      return `${this.task.task_cost.payment_currency} ${this.task.task_cost.cost_estimation}`;
    },

    trueInternalCost () {
      return `${this.task.users[0].payment_currency} ${this.task.internal_cost}`;
    },

    dueDate () {
      if (this.task.task_cost === null || this.task.task_cost.estimated_completion_date === null) {
        return 'N/A';
      }

      return moment(this.task.task_cost.estimated_completion_date).format('Do MMMM YYYY');
    },

    subtasks () {
      if (this.task.subtasks === null || this.task.subtasks.length === 0) {
        return [];
      }

      return this.task.subtasks.map((task) => {
        return {
          id: task.id,
          title: task.title,
          totalScreenshots: 0,
          totalTime: '',
          status: task.completed_at === null ? 'pending' : 'completed',
        };
      });
    },

    requiresScreenshots () {
      return this.currentUser.requires_screenshot ?? false;
    },

    ...mapState(['currentUser']),
  },

  watch: {
    currentUser () {
      this.fetchLogs();
    },
  },

  methods: {
    minutesToHoursFormatter: minutesToHoursFormatter,

    handleBackButton () {
      window.history.back();
    },

    async fetchLogs () {
      this.loading = true;
      const res = await axios.get(`/reports/tasks/${this.task.id_hash}/log`, {
        params: {
          start_date: localStorage.getItem('wfh_tm_sd'),
          end_date: localStorage.getItem('wfh_tm_ed'),
        }
      });

      for (const activity of res.data.data) {
        for (const log of activity.logs) {
          if (log.hasOwnProperty('screenshots')) {
            for (const screenshot of log.screenshots) {
              if (screenshot.confirmed_at === null) {
                this.requireSubmission = true;
                break;
              }
            }
          }

          if (this.requireSubmission === true) {
            break;
          }
        }

        if (this.requireSubmission === true) {
          break;
        }
      }

      this.originalLogs = JSON.parse(JSON.stringify(res.data.data));
      this.logs = res.data.data;
      this.loading = false;
    },

    async submitLogs () {
      await axios.post(route('screenshots.submit'), {
        task_id: this.task.id,
        deleted_screenshots: Object.entries(this.deletedScreenshots).map((screenshot) => {
          return {
            playable_id: screenshot[0],
            screenshot_ids: screenshot[1],
          };
        }),
      });

      window.location.reload();
    },

    cancelEditing () {
      this.logs = JSON.parse(JSON.stringify(this.originalLogs));
      this.deletedScreenshots = {};
    },

    deleteScreenshot (activityKey, logKey, screenshot) {
      const activity = this.logs[activityKey].logs[logKey];

      activity.screenshots = activity.screenshots.filter((screenshotFilter) => {
        return screenshotFilter.id !== screenshot.id;
      });

      if (this.deletedScreenshots.hasOwnProperty(activity.playable_id)) {
        this.deletedScreenshots[activity.playable_id].push(screenshot.id);
      } else {
        this.deletedScreenshots[activity.playable_id] = [screenshot.id];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.note {
  color: #ff375e;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: italic;
  letter-spacing: 0;
  line-height: 17px;
}

.task-details-title {
  color: #303030;
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 28px;
}

.task-details-description {
  color: #808fa4;
  font-family: ".SF NS Display", sans-serif;
  font-size: 14px;
  letter-spacing: 0.5px;
  line-height: 20px;
}

.task-details-company {
  color: #8997aa;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  letter-spacing: 0;
  line-height: 22px;
}

.task-details-estimation-label {
  color: #808fa4;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 22px;
}

.task-details-estimation {
  color: #303030;
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  letter-spacing: 0;
  line-height: 24px;
}
</style>
